/**
 * Payouts hooks — TanStack Query wrappers around payoutService.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { payoutService } from '@/lib/services/payout.service'
import { useAuth } from '@/lib/hooks/use-auth'
import type { PayoutAccountInput } from '@/lib/types/payout.types'

const payoutKeys = {
  all: ['payouts'] as const,
  accounts: (userId: string) => [...payoutKeys.all, 'accounts', userId] as const,
  history: (userId: string) => [...payoutKeys.all, 'history', userId] as const,
}

export function usePayoutAccounts() {
  const { user } = useAuth()
  return useQuery({
    queryKey: payoutKeys.accounts(user?.id ?? ''),
    queryFn: () => payoutService.listAccounts(),
    enabled: !!user?.id,
  })
}

export function usePayoutHistory() {
  const { user } = useAuth()
  return useQuery({
    queryKey: payoutKeys.history(user?.id ?? ''),
    queryFn: () => payoutService.listPayouts(user!.id),
    enabled: !!user?.id,
  })
}

export function useCreatePayoutAccount() {
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PayoutAccountInput) => {
      if (!user?.id) throw new Error('Not authenticated')
      return payoutService.createPayoutAccount(user.id, input)
    },
    onSuccess: () => {
      if (user?.id) {
        void qc.invalidateQueries({ queryKey: payoutKeys.accounts(user.id) })
      }
    },
  })
}

export function useSetDefaultPayoutAccount() {
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) => {
      if (!user?.id) throw new Error('Not authenticated')
      return payoutService.setDefaultAccount(accountId)
    },
    onSuccess: () => {
      if (user?.id) {
        void qc.invalidateQueries({ queryKey: payoutKeys.accounts(user.id) })
      }
    },
  })
}

// ─── Organization-scoped payout hooks ─────────────────────────────────────
// Parallel to the individual-host hooks. Only fleet_manager of the org
// can call these (enforced by RLS + edge function guard).

const payoutKeysOrg = {
  all: ['payouts', 'org'] as const,
  accounts: (orgId: string) => [...payoutKeysOrg.all, 'accounts', orgId] as const,
  history: (orgId: string) => [...payoutKeysOrg.all, 'history', orgId] as const,
  earnings: (orgId: string) => [...payoutKeysOrg.all, 'earnings', orgId] as const,
}

export function usePayoutAccountsForOrg(orgId: string | undefined) {
  return useQuery({
    queryKey: payoutKeysOrg.accounts(orgId ?? ''),
    queryFn: () => payoutService.listAccountsForOrg(orgId!),
    enabled: !!orgId,
  })
}

export function usePayoutHistoryForOrg(orgId: string | undefined) {
  return useQuery({
    queryKey: payoutKeysOrg.history(orgId ?? ''),
    queryFn: () => payoutService.listPayoutsForOrg(orgId!),
    enabled: !!orgId,
  })
}

export function useCreatePayoutAccountForOrg(orgId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PayoutAccountInput) => {
      if (!orgId) throw new Error('Missing org id')
      return payoutService.createPayoutAccountForOrg(orgId, input)
    },
    onSuccess: () => {
      if (orgId) {
        void qc.invalidateQueries({ queryKey: payoutKeysOrg.accounts(orgId) })
      }
    },
  })
}

export function useSetDefaultPayoutAccountForOrg(orgId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) => {
      if (!orgId) throw new Error('Missing org id')
      return payoutService.setDefaultAccountForOrg(orgId, accountId)
    },
    onSuccess: () => {
      if (orgId) {
        void qc.invalidateQueries({ queryKey: payoutKeysOrg.accounts(orgId) })
      }
    },
  })
}

export function useOrgEarnings(orgId: string | undefined) {
  return useQuery({
    queryKey: payoutKeysOrg.earnings(orgId ?? ''),
    queryFn: () => payoutService.getOrgEarningsSummary(orgId!),
    enabled: !!orgId,
    staleTime: 60 * 1000,
  })
}
